import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";

const MIN_PASSWORD_LENGTH = 8;

const INPUT_BASE =
  "mt-2 mb-3 flex h-12 w-full items-center justify-center rounded-xl bg-white/5 p-3 text-sm text-white outline-none transition-colors placeholder:text-gray-400";
const INPUT_BORDER_OK = "border border-white/20";
const INPUT_BORDER_ERR = "border-2 border-red-500 dark:border-red-400";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errorText, setErrorText] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorText("Invalid reset link. Request a new link from the sign-in page.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");
    if (!token) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorText(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setErrorText("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/password-reset"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setDone(true);
        setTimeout(() => navigate("/SignIn", { replace: true }), 2500);
        return;
      }
      setErrorText(
        clientSafeMessage(data.message, "Could not reset password."),
      );
    } catch {
      setErrorText("Network error. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="relative z-30 mt-16 mb-16 flex min-h-[65vh] w-full flex-col items-center justify-center gap-6 px-6 text-center md:mx-0 md:px-8 lg:mb-10">
        <h2 className="text-4xl font-extrabold leading-tight text-green-400 sm:text-5xl md:text-6xl">
          Password updated
        </h2>
        <p className="text-base text-gray-300">
          Redirecting to sign in…
        </p>
      </div>
    );
  }

  return (
    <div className="relative z-30 mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-white">New password</h4>
        <p className="mb-9 ml-1 text-base text-gray-300">
          Choose a new password for your account.
        </p>
        <form onSubmit={handleSubmit} className="w-full">
          <label className="ml-1.5 text-sm font-medium text-gray-100">
            New password
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errorText) setErrorText("");
              }}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              className={`${INPUT_BASE} ${errorText ? INPUT_BORDER_ERR : INPUT_BORDER_OK}`}
            />
          </label>
          <label className="ml-1.5 text-sm font-medium text-gray-100">
            Confirm password
            <input
              name="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (errorText) setErrorText("");
              }}
              placeholder="Re-enter your password"
              className={`${INPUT_BASE} ${errorText ? INPUT_BORDER_ERR : INPUT_BORDER_OK}`}
            />
          </label>
          {errorText && (
            <p className="mb-3 text-sm text-red-400">{errorText}</p>
          )}
          <button
            type="submit"
            disabled={loading || !token}
            className="linear mb-6 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
          >
            {loading ? "Saving…" : "Save new password"}
          </button>
        </form>
        <Link
          to="/SignIn"
          className="text-sm text-brand-400 hover:text-brand-300"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
